// ============================================
// TRT score helpers — LTRS, qADAM, BMI
// ============================================
//
// Per docs/BACKEND_TRT_LAB_INTEGRATION_PRD.md §3:
//   - LTRS: 0–32 (15 slides + BMI band)
//   - qADAM: 10–50 (10 slides, 1–5 each)
//   - BMI:   (weight_lb / height_in²) × 703, rounded 1dp
//
// Backend re-computes from raw answers and rejects on mismatch (TRT_SCORE_MISMATCH).
// Tolerance: BMI ±0.2, LTRS / qADAM exact.

import {
  TRT_LTRS_SLIDE_IDS,
  TRT_QADAM_SLIDE_IDS,
  trtFlowById,
} from '../data/trtData';

/**
 * Look up the point value for a chosen option on a single-select slide.
 * Returns 0 when the slide / option / points field is missing — that way
 * an unanswered slide simply doesn't contribute.
 */
const pointsFor = (slideId, answer) => {
  const slide = trtFlowById[slideId];
  if (!slide || !Array.isArray(slide.options)) return 0;
  const opt = slide.options.find((o) => o.value === answer);
  if (!opt || typeof opt.points !== 'number') return 0;
  return opt.points;
};

/**
 * BMI band → LTRS points (PRD §3.1).
 *   <25      → 0
 *   25–29.9  → 2
 *   ≥30      → 3
 */
const bmiBandPoints = (bmi) => {
  if (typeof bmi !== 'number' || Number.isNaN(bmi)) return 0;
  if (bmi < 25) return 0;
  if (bmi < 30) return 2;
  return 3;
};

/**
 * Compute BMI from a height-weight answer object
 * (the shape produced by HeightWeightBmiStep:
 *   { feet, inches, weight, bmi? }).
 * Returns null when inputs are incomplete.
 */
export const computeBmi = (heightWeightAnswer) => {
  if (!heightWeightAnswer) return null;
  const { feet, inches, weight } = heightWeightAnswer;
  const totalInches = parseInt(feet || 0, 10) * 12 + parseInt(inches || 0, 10);
  const w = parseFloat(weight);
  if (!totalInches || !w) return null;
  const bmi = (w / (totalInches * totalInches)) * 703;
  return Math.round(bmi * 10) / 10;
};

/**
 * Compute LTRS total (0–32) from the answers map.
 * Sums per-slide option points + the BMI band.
 */
export const computeLtrs = (answers) => {
  let total = 0;
  for (const slideId of TRT_LTRS_SLIDE_IDS) {
    total += pointsFor(slideId, answers[slideId]);
  }
  // BMI band — derive from height-weight slide
  const bmi = computeBmi(answers['height-weight']);
  if (bmi !== null) total += bmiBandPoints(bmi);
  return total;
};

/**
 * Compute qADAM total (10–50) from the answers map.
 * Each of the 10 slides contributes 1–5 based on the chosen option's `points`.
 * Unanswered slides contribute 0, which is below the legal minimum (10);
 * callers should ensure all 10 slides are answered before submission.
 */
export const computeQadam = (answers) => {
  let total = 0;
  for (const slideId of TRT_QADAM_SLIDE_IDS) {
    total += pointsFor(slideId, answers[slideId]);
  }
  return total;
};

/**
 * Build the `scores` envelope object the backend expects:
 *   { ltrsTotal, qadamTotal, bmi }
 *
 * Pass the full answers map. `bmi` is omitted (sent as null) when the
 * height-weight slide hasn't been completed.
 */
export const computeTrtScores = (answers) => {
  const bmi = computeBmi(answers['height-weight']);
  return {
    ltrsTotal: computeLtrs(answers),
    qadamTotal: computeQadam(answers),
    bmi: bmi !== null ? bmi : null,
  };
};
